import { useEffect, useState } from "react";
import { Wallet, Loader2, Send, ChevronDown } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa } from "../../lib/format";
import { toast } from "sonner";

export function VendeurCaisseCard() {
  const [data, setData] = useState(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [network, setNetwork] = useState("FLOOZ");
  const [phone, setPhone] = useState("");
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [options, setOptions] = useState([]);

  const load = () => api.get("/vendeur/caisse/me").then(({ data }) => setData(data)).catch(() => setData(false));
  const loadOptions = () => api.get("/vendeur/caisse/order-options").then(({ data }) => setOptions(data)).catch(() => setOptions([]));
  useEffect(() => { load(); loadOptions(); }, []);

  const pollStatus = (orderId) => {
    setChecking(true);
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/vendeur/caisse/paygate-status/${orderId}`);
        if (data.status === "paye") {
          clearInterval(timer); setChecking(false);
          toast.success("Argent reçu dans PayGate ✅ Vente versée.");
          load(); loadOptions(); setOrderNumber("");
        } else if (data.status === "echoue") {
          clearInterval(timer); setChecking(false);
          toast.error("Le paiement PayGate a échoué ou a été annulé. La vente reste en caisse.");
          loadOptions();
        }
      } catch { /* ignore */ }
      if (tries >= 40) { clearInterval(timer); setChecking(false); loadOptions(); }
    }, 4000);
  };

  const payViaPaygate = async () => {
    if (!orderNumber.trim()) return toast.error("Choisissez une vente");
    if (phone.replace(/\D/g, "").length < 8) return toast.error("Entrez le numéro Mobile Money (8 chiffres)");
    setPaying(true);
    try {
      const { data } = await api.post("/vendeur/caisse/paygate-pay", { order_number: orderNumber.trim(), network, phone });
      toast.success(data.message || "Demande envoyée sur votre téléphone. Validez le paiement.");
      pollStatus(data.order_id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPaying(false); }
  };

  if (data === null) return <div className="bg-white border border-brand-line rounded-2xl p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  if (data === false) return null;

  return (
    <div data-testid="vendeur-caisse-card" className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-xl bg-brand/15 text-brand flex items-center justify-center"><Wallet className="w-5 h-5" /></span>
        <div>
          <h2 className="font-heading font-bold text-brand-ink">Ma caisse</h2>
          <p className="text-xs text-brand-muted">Argent liquide encaissé et versements à faire.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        <div className="rounded-xl bg-brand-cream border border-brand-line p-4">
          <p className="text-xs text-brand-muted">Encaissé</p>
          <p className="font-heading font-black text-xl text-brand-ink mt-1">{fcfa(data.collected)}</p>
        </div>
        <div className="rounded-xl bg-green-50 border border-green-100 p-4">
          <p className="text-xs text-green-800">Déjà versé</p>
          <p className="font-heading font-black text-xl text-green-900 mt-1">{fcfa(data.deposited)}</p>
        </div>
        <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
          <p className="text-xs text-blue-800">En attente</p>
          <p className="font-heading font-black text-xl text-blue-900 mt-1">{fcfa(data.pending)}</p>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-4" data-testid="caisse-balance">
          <p className="text-xs text-amber-800">Solde à remettre</p>
          <p className="font-heading font-black text-xl text-amber-900 mt-1">{fcfa(data.balance)}</p>
        </div>
      </div>

      {/* Payer une vente via PayGate (argent sécurisé dans le compte PayGate du Patron) */}
      <div className="mt-5 border border-brand-line rounded-xl p-4">
        <p className="font-semibold text-brand-ink text-sm">Verser l'argent dans PayGate</p>
        <p className="text-xs text-brand-muted mb-3">Choisissez la <b>vente</b> encaissée, l'<b>opérateur</b> (Flooz ou T-Money) et le <b>numéro Mobile Money</b> qui paie. Une demande arrive sur ce téléphone : validez avec votre code secret. L'argent va directement dans le compte PayGate du Patron.</p>
        <div className="space-y-2">
          <select value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} data-testid="versement-order-select"
            className="border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 w-full bg-white">
            <option value="">Choisir une vente…</option>
            {options.map((o) => (
              <option key={o.id} value={o.order_number}>
                {o.order_number} · {o.customer_name || "client"} · {fcfa(o.total)} · {o.payment_status === "paye" ? "payé" : "à encaisser"}
              </option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select value={network} onChange={(e) => setNetwork(e.target.value)} data-testid="paygate-network-select"
              className="border border-brand-line rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/30">
              <option value="FLOOZ">Flooz (Moov)</option>
              <option value="TMONEY">T-Money (Yas)</option>
            </select>
            <div className="flex">
              <span className="inline-flex items-center px-2.5 rounded-l-lg border border-r-0 border-brand-line bg-brand-cream text-brand-sub text-xs font-semibold">+228</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="paygate-phone-input" placeholder="90 00 00 00"
                className="border border-brand-line rounded-r-lg px-3 py-2.5 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-brand/30" />
            </div>
          </div>
          <button onClick={payViaPaygate} disabled={paying || checking} data-testid="paygate-pay-btn"
            className="w-full bg-brand text-white hover:bg-brand-hover rounded-lg px-4 py-2.5 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {(paying || checking) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Payer via PayGate
          </button>
        </div>
        <p className="text-xs text-brand-muted mt-2">
          {checking ? "En attente de la validation du paiement sur votre téléphone…" :
            options.length === 0 ? "Aucune vente à verser pour le moment." : `${options.length} vente(s) à verser.`}
        </p>
      </div>

      <button onClick={() => setShowHistory((v) => !v)} data-testid="caisse-history-toggle" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:gap-2 transition-all">
        Historique versements & caisse <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? "rotate-180" : ""}`} />
      </button>

      {showHistory && (
        <div className="mt-3 grid md:grid-cols-2 gap-4" data-testid="caisse-history">
          <div>
            <p className="text-xs font-semibold text-brand-muted mb-2">MES VERSEMENTS</p>
            {data.deposits.length === 0 ? <p className="text-sm text-brand-muted">Aucun versement.</p>
              : data.deposits.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-brand-line rounded-lg px-3 py-2 mb-1.5 text-sm">
                  <span>{fcfa(d.amount)} <span className="text-brand-muted">· {d.operator === "flooz" ? "Moov" : "Togocel"}{d.order_number ? ` · ${d.order_number}` : ""}</span></span>
                  <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${d.status === "valide" ? "bg-green-100 text-green-700" : d.status === "rejete" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                    {d.status === "valide" ? "validé" : d.status === "rejete" ? "rejeté" : "en attente"}
                  </span>
                </div>
              ))}
          </div>
          <div>
            <p className="text-xs font-semibold text-brand-muted mb-2">CAISSE PAR JOUR</p>
            {data.report.length === 0 ? <p className="text-sm text-brand-muted">Aucune vente cash.</p>
              : data.report.map((r) => (
                <div key={r.date} className="flex items-center justify-between border border-brand-line rounded-lg px-3 py-2 mb-1.5 text-sm">
                  <span className="text-brand-sub">{new Date(r.date).toLocaleDateString("fr-FR")}</span>
                  <span className="font-semibold text-brand-ink">{fcfa(r.collected)}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
